import Vue from 'vue'
import axios from 'axios'

/**
 * Membuat instance Vue untuk menangani pengambilan data secara paginasi (bertahap).
 *
 * Fungsi ini mengembalikan objek Vue yang memiliki state dan method `fetchData`
 * untuk melakukan pemanggilan data menggunakan fungsi `fetchFn`.
 *
 * @param {(params: { offset: number, limit: number, [key: string]: any }) => Promise<{ data: any[] }>} fetchFn
 *   Fungsi async yang digunakan untuk mengambil data, menerima `offset`, `limit`, dan parameter opsional lainnya.
 * @param {number} [defaultOffset=0] - Offset awal untuk paginasi.
 * @param {number} [defaultLimit=10] - Jumlah data per halaman yang diambil.
 *
 * @returns {Vue} Objek Vue dengan data reaktif dan method `fetchData` untuk mengambil data secara paginasi.
 *
 * @example
 * const fetcher = createPaginatedFetcher(api.getUsers)
 * fetcher.fetchData()
 *
 * @example
 * // Reset data dan ambil ulang dari awal
 * fetcher.fetchData({ reset: true })
 *
 * @example
 * // Ambil data dengan opsi tambahan
 * fetcher.fetchData({ options: { search: 'abc' } })
 *
 * @example
 * // Cancel request
 * fetcher.cancel()
 */
// eslint-disable-next-line import/prefer-default-export
export function createPaginatedFetcher(fetchFn, defaultOffset = 0, defaultLimit = 50) {
  return new Vue({
    data() {
      return {
        data: [],
        offset: defaultOffset,
        limit: defaultLimit,
        isLoading: false,
        error: null,
        hasMore: false,
        options: {},
        cancelTokenSource: null,
        currentRequestId: 0,
      }
    },
    methods: {
      cancel() {
        if (this.cancelTokenSource) {
          this.cancelTokenSource.cancel('Request cancelled')
          this.cancelTokenSource = null
        }
      },
      async fetchData({ reset = false, options = {} } = {}) {
        this.cancel()

        this.currentRequestId += 1
        const requestId = this.currentRequestId

        if (reset) {
          this.offset = defaultOffset
          this.data = []
          this.hasMore = false
          this.options = options
        }

        if (reset && options.limit) {
          this.limit = options.limit
        }

        if (!reset && (!this.hasMore && this.offset > 0)) return

        this.cancelTokenSource = axios.CancelToken.source()
        const cancelToken = this.cancelTokenSource.token

        this.isLoading = true
        this.error = null

        try {
          const result = await fetchFn({
            offset: this.offset,
            limit: this.limit,
            cancelToken,
            ...this.options,
          })

          if (requestId !== this.currentRequestId) {
            return
          }

          const items = result?.data || []

          this.data.push(...items)
          this.offset += items.length
          this.hasMore = items.length >= this.limit
        } catch (err) {
          if (axios.isCancel(err)) {
            return
          }
          if (requestId !== this.currentRequestId) {
            return
          }
          this.error = err
        } finally {
          if (requestId === this.currentRequestId) {
            this.isLoading = false
            this.cancelTokenSource = null
          }
        }
      },
    },
  })
}
